// ABOUTME: Cloudflare Workers database provider mapping table operations to REST API calls
// ABOUTME: Implements IDatabaseProvider by translating table names to versioned API routes

import type {
  IDatabaseProvider,
  QueryOptions,
  DatabaseResult,
  QueryFilter,
  RealtimeCallback,
} from '../interfaces'

const TABLE_ROUTES: Record<string, string> = {
  'wardrobe_items': '/api/v1/wardrobe',
  'outfits': '/api/v1/outfits',
  'wear_logs': '/api/v1/wear-logs',
  'challenge_progress': '/api/v1/challenges',
  'module_completions': '/api/v1/modules',
  'profiles': '/api/v1/profile',
  'game_state': '/api/v1/game-state',
}

function getRoute(table: string): string {
  const route = TABLE_ROUTES[table]
  if (!route) {
    throw new Error(`Unknown table: ${table}. Valid tables: ${Object.keys(TABLE_ROUTES).join(', ')}`)
  }
  return route
}

function buildQueryParams(options?: QueryOptions): string {
  if (!options) return ''

  const params = new URLSearchParams()

  if (options.filters) {
    for (const filter of options.filters) {
      params.set(`filter[${filter.field}][${filter.operator}]`, String(filter.value))
    }
  }

  if (options.orderBy) {
    params.set('orderBy', options.orderBy.field)
    params.set('order', options.orderBy.ascending ? 'asc' : 'desc')
  }

  if (options.limit !== undefined) {
    params.set('limit', String(options.limit))
  }

  if (options.offset !== undefined) {
    params.set('offset', String(options.offset))
  }

  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export class CloudflareDatabaseProvider implements IDatabaseProvider {
  private apiUrl: string

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl
  }

  async query<T>(table: string, options?: QueryOptions): Promise<T[]> {
    const route = getRoute(table)
    const queryString = buildQueryParams(options)

    const response = await fetch(`${this.apiUrl}${route}${queryString}`, {
      method: 'GET',
      credentials: 'include',
    })

    if (!response.ok) {
      throw new Error(`Query failed for ${table}: ${response.status}`)
    }

    return await response.json() as T[]
  }

  async get<T>(table: string, id: string): Promise<T | null> {
    const route = getRoute(table)

    const response = await fetch(`${this.apiUrl}${route}/${id}`, {
      method: 'GET',
      credentials: 'include',
    })

    if (!response.ok) {
      if (response.status === 404) return null
      throw new Error(`Get failed for ${table}/${id}: ${response.status}`)
    }

    return await response.json() as T
  }

  async insert<T>(table: string, data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>): Promise<DatabaseResult<T>> {
    const route = getRoute(table)

    try {
      const response = await fetch(`${this.apiUrl}${route}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { data: null, error: new Error((errorData as { message?: string }).message || `Insert failed: ${response.status}`) }
      }

      const result = await response.json() as T
      return { data: result }
    } catch (err) {
      return { data: null, error: err instanceof Error ? err : new Error('Insert failed') }
    }
  }

  async update<T>(table: string, id: string, data: Partial<T>): Promise<DatabaseResult<T>> {
    const route = getRoute(table)

    try {
      const response = await fetch(`${this.apiUrl}${route}/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { data: null, error: new Error((errorData as { message?: string }).message || `Update failed: ${response.status}`) }
      }

      const result = await response.json() as T
      return { data: result }
    } catch (err) {
      return { data: null, error: err instanceof Error ? err : new Error('Update failed') }
    }
  }

  async delete(table: string, id: string): Promise<{ error?: Error }> {
    const route = getRoute(table)

    try {
      const response = await fetch(`${this.apiUrl}${route}/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        return { error: new Error((errorData as { message?: string }).message || `Delete failed: ${response.status}`) }
      }

      return {}
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Delete failed') }
    }
  }

  async batchInsert<T>(table: string, data: Array<Omit<T, 'id' | 'createdAt' | 'updatedAt'>>): Promise<DatabaseResult<T[]>> {
    const results: T[] = []

    for (const item of data) {
      const result = await this.insert<T>(table, item)
      if (result.error) {
        return { data: null, error: result.error }
      }
      if (result.data) {
        results.push(result.data)
      }
    }

    return { data: results }
  }

  subscribe<T>(_table: string, _callback: RealtimeCallback<T>, _filters?: QueryFilter[]): () => void {
    return () => {}
  }

  // biome-ignore lint/suspicious/noExplicitAny: transaction data is dynamic
  async transaction<T>(operations: Array<{ type: 'insert' | 'update' | 'delete'; table: string; data: any }>): Promise<DatabaseResult<T>> {
    // biome-ignore lint/suspicious/noExplicitAny: collecting mixed results
    const results: any[] = []

    for (const op of operations) {
      if (op.type === 'insert') {
        const result = await this.insert(op.table, op.data)
        if (result.error) return { data: null, error: result.error }
        results.push(result.data)
      } else if (op.type === 'update') {
        const result = await this.update(op.table, op.data.id, op.data)
        if (result.error) return { data: null, error: result.error }
        results.push(result.data)
      } else if (op.type === 'delete') {
        const result = await this.delete(op.table, op.data.id)
        if (result.error) return { data: null, error: result.error }
      }
    }

    return { data: results as T }
  }
}
